<template>
  <div class="onboarding-pact">
    <Transition name="flip" mode="out-in">
      <div :key="`${isCardFlipped}`" class="onboarding-pact__cards-wrapper">
        <svg
          class="onboarding-pact__sparkle"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 43 55"
        >
          <path
            d="M42.845 19.801a36.335 36.335 0 0 1-6.607-6.632A36.846 36.846 0 0 1 29.89.3a.415.415 0 0 0-.395-.3.415.415 0 0 0-.396.3 37.327 37.327 0 0 1-6.472 13.004 37.04 37.04 0 0 1-6.472 6.497.43.43 0 0 0-.155.33c0 .126.068.252.164.33a34.669 34.669 0 0 1 6.473 6.235c3.033 3.81 5.216 8.184 6.472 12.994.048.184.212.31.396.31a.413.413 0 0 0 .396-.32c1.072-4.761 3.217-9.25 6.202-12.975a31.174 31.174 0 0 1 6.723-6.225.405.405 0 0 0 .174-.33.45.45 0 0 0-.155-.34v-.009ZM14.914 43.89a20.084 20.084 0 0 1-3.67-3.647 20.184 20.184 0 0 1-3.527-7.078.23.23 0 0 0-.22-.165.23.23 0 0 0-.22.165 20.449 20.449 0 0 1-3.595 7.152 20.478 20.478 0 0 1-3.596 3.574.235.235 0 0 0-.086.181c0 .07.038.139.091.181a19.177 19.177 0 0 1 3.596 3.43 19.108 19.108 0 0 1 3.596 7.146.227.227 0 0 0 .22.171.229.229 0 0 0 .22-.176 17.595 17.595 0 0 1 3.445-7.136 17.25 17.25 0 0 1 3.735-3.424.222.222 0 0 0 .097-.181.246.246 0 0 0-.086-.187v-.005Z"
            fill="#FFC906"
          />
        </svg>

        <div class="onboarding-pact__cards">
          <div
            v-for="n in 2"
            :key="n"
            class="onboarding-pact__card"
            :class="{
              'onboarding-pact__card--front': n === 1,
              'onboarding-pact__card--back': n === 2,
              'onboarding-pact__card--visible':
                (n === 1 && !isCardFlipped) || (n === 2 && isCardFlipped),
            }"
          >
            <h1 v-if="n === 2" class="onboarding-pact__title">Note to Self</h1>
            <h1 v-else class="onboarding-pact__title" v-html="page.title"></h1>

            <div
              class="onboarding-pact__text"
              v-html="getCardText(n === 1)"
            ></div>

            <div v-if="n === 2" class="onboarding-pact__footer">
              <p class="onboarding-pact__text-footer">
                Thanks,<br />You from the future
              </p>
              <p class="onboarding-pact__future-date">
                {{ futureDateText }}
              </p>
              <CommitmentFingerprint class="onboarding-pact__fingerprint" />
            </div>

            <div v-else class="onboarding-pact__button-wrapper">
              <AppButton
                class="onboarding-pact__button"
                :disabled="isCardFlipped"
                @click="flipCard"
              >
                Commit
              </AppButton>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { QuestionnaireView } from '@/models/onboarding'
import { CommitmentPactPage } from '@/models/onboarding.types'
import { PublicInterface } from '@/models/interfaces'

import AppButton from '@/app/components/button/AppButton.vue'
import CommitmentFingerprint from '../CommitmentFingerprint.vue'

const props = defineProps<{
  view: PublicInterface<QuestionnaireView>
}>()

const page = computed(() => {
  return props.view.currentPage as CommitmentPactPage
})

const isCardFlipped = ref(false)

function getCardText(isFrontFace: boolean): string {
  const initialText = isFrontFace
    ? page.value.textInitial
    : page.value.textFinal

  const parsedText = initialText
    .split('<br>')
    .map((block) => `<p>${block}</p>`)
    .join('')

  if (isFrontFace) {
    return parsedText
  } else {
    return parsedText
      .replace(/{{futureYear}}/g, futureYear)
      .replace(/{{currentDate}}/g, currentDateText)
  }
}

const currentDate = new Date()
const futureDate = new Date(currentDate)
futureDate.setFullYear(currentDate.getFullYear() + 1)

const currentDateText = currentDate.toLocaleDateString('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
})
const futureDateText = futureDate.toLocaleDateString('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
})
const futureYear = futureDate.getFullYear().toString()

function flipCard(): void {
  isCardFlipped.value = true
  setTimeout(() => {
    props.view.showContinueButton()
  }, 2000)
}
</script>

<style lang="scss" scoped>
.onboarding-pact {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: var(--onboarding-padding);
  padding-bottom: calc(56px + var(--onboarding-padding) * 2);
  flex: 1;
  overflow-y: auto;

  &:has(
    + .onboarding-questionnaire__button-wrapper:not(.button-fade-enter-active)
  ) {
    padding-bottom: calc(var(--onboarding-padding) * 2);
  }

  &__cards-wrapper {
    margin-block: auto;
    position: relative;
    width: 100%;
    max-width: var(--onboarding-outer-width);
    perspective: 1000px;
    @media only screen and (min-width: $w-tablet-b) {
      max-width: 460px;
    }
  }

  &__cards {
    display: grid;
    grid-template-areas: 'stack';
    width: 100%;
    transform-style: preserve-3d;
  }

  &__sparkle {
    position: absolute;
    right: 28px;
    top: -28px;
    z-index: 2;
    width: 43px;
  }

  &__card {
    grid-area: stack;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 28px;
    background: $c-white;
    border: 1px solid $c-border-7;
    border-radius: 36px;

    @media only screen and (max-height: 760px) {
      padding: 24px;
    }
    transform: rotateY(180deg);
  }

  &__card--back {
    background: $c-yellow-2;
    border-color: $c-yellow;
  }

  &__card--visible {
    z-index: 1;
    transform: rotateY(0deg);
    backface-visibility: hidden;
    filter: drop-shadow(0px 1px 5px rgba(255, 255, 255, 0.1))
      drop-shadow(0px 8px 15px rgba(20, 28, 49, 0.1));
  }

  &__title {
    font-size: 24px;
    line-height: 30px;
    letter-spacing: -0.02em;
    color: $c-black-2;

    .istar {
      color: $c-yellow;
    }
  }

  &__text {
    :deep(p) {
      font-size: 16px;
      line-height: 26px;
      letter-spacing: -0.02em;
      color: $c-black-15;
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__footer {
    position: relative;
    padding-top: 17px;
    margin-top: auto;

    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      width: 72px;
      height: 1px;
      background: $c-black-6;
    }
  }

  &__text-footer {
    margin-bottom: 16px;
  }

  &__future-date {
    width: fit-content;
    padding: 6px 12px;
    border-radius: 99px;
    margin-bottom: 0;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: -0.02em;
    background: $c-border-7;
  }

  &__fingerprint {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 56px;
    --stroke-color: #{$c-yellow};
  }

  &__button-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 32px;
    border-radius: 20px;
    border: 1px solid $c-yellow;
    background: $c-yellow-2;
  }

  &__button {
    padding-block: 8px;
    font-size: 18px;
    line-height: 22px;
  }
}

.flip {
  &-leave-active,
  &-leave-active .onboarding-pact__cards {
    transition: all 2s cubic-bezier(0, 1.207, 0.87, 1);
  }

  &-leave-active {
    .onboarding-pact__sparkle {
      transition: 0.1s ease;
    }

    .onboarding-pact__card {
      pointer-events: none;
      user-select: none;
      filter: drop-shadow(0px 1px 5px rgba(255, 255, 255, 0.1))
        drop-shadow(0px 8px 15px rgba(20, 28, 49, 0.1));
    }

    .onboarding-pact__button {
      cursor: auto;
    }
  }

  &-leave-to {
    .onboarding-pact__cards {
      rotate: y calc(180deg * 5);
    }

    .onboarding-pact__sparkle {
      opacity: 0;
    }
  }

  &-enter-active,
  &-enter-active .onboarding-pact__sparkle {
    transition: all 0.3s ease;
  }

  &-enter-from {
    .onboarding-pact__sparkle {
      opacity: 0;
    }
  }

  &-enter-to {
    .onboarding-pact__sparkle {
      opacity: 1;
    }
  }
}
</style>
